import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

interface Labels {
  unit_number: string;
  locality: string;
  administrative_division_1: string;
  administrative_division_2: string;
  postal_code: string;
}

export function useAddressLabels() {
  const { t } = useTranslation();

  const labels: Record<string, Labels> = useMemo(
    () => ({
      CA: {
        unit_number: t("Apartment or unit number"),
        locality: t("City or town"),
        administrative_division_1: t("Province or territory"),
        administrative_division_2: t("County"),
        postal_code: t("Postal code"),
      },
      GB: {
        unit_number: t("Address line 2"),
        locality: t("City or town"),
        administrative_division_1: t("Nation"),
        administrative_division_2: t("County"),
        postal_code: t("Postcode"),
      },
      AU: {
        unit_number: t("Apartment or unit number"),
        locality: t("City or municipality"),
        administrative_division_1: t("State or territory"),
        administrative_division_2: t("County"),
        postal_code: t("Postcode"),
      },
      US: {
        unit_number: t("Apartment or unit number"),
        locality: t("City or town"),
        administrative_division_1: t("State"),
        administrative_division_2: t("County"),
        postal_code: t("Zipcode"),
      },
      default: {
        unit_number: t("Unit number"),
        locality: t("Locality"),
        administrative_division_1: t("Region"),
        administrative_division_2: t("County"),
        postal_code: t("Postal code"),
      },
    }),
    [t]
  );

  const getLabels = useCallback(
    (country?: string) => {
      if (!country || !labels[country]) {
        return labels["default"];
      }

      return labels[country];
    },
    [labels]
  );

  return {
    labels,
    getLabels,
  };
}

export function useAddressLabelsForCountry(country?: string) {
  const { getLabels } = useAddressLabels();
  return getLabels(country);
}
